import React from 'react';
import { AccordionItem, AccordionContent } from '@/components/ui/accordion';
import PieGraph from '@/components/organisms/graphs/PieGraph';
import chevronDownIcon from '@/assets/icons/chevron-down.svg';
import { ACCOUNTING_CENTERS_ICONS, ACType } from '@/constants/accounting-centers';
import { calculateChartData, getChartConfigs } from '@/utils/chartCalculations';
import { useNavigate, useParams } from 'react-router-dom';
import { PAGE } from '@/routes';

interface ACItemAccordionProps {
  ac: any;
  acCenter: {
    type: ACType;
    acmev?: number;
    acmev_used?: number;
    acrt_achieved?: number;
  };
  graphViewMode: string;
  open: boolean;
  onToggle: () => void;
}

const ACItemAccordion = ({ ac, acCenter, graphViewMode, open, onToggle }: ACItemAccordionProps) => {
  const { acmevChartData, acrtChartData } = calculateChartData(ac, '#7663EB');
  const { acmevChartConfig, acrtChartConfig } = getChartConfigs('primary');
  const navigate = useNavigate();
  const { budgetId, acType } = useParams();

  return (
    <AccordionItem value={ac._id} className="border rounded-[8px] min-h-[88px] transition-all duration-300">
      <div className="flex items-center select-none py-0 pr-3">
        <div className="px-5">
          <img className="w-[40px] h-[40px]" src={ACCOUNTING_CENTERS_ICONS[acCenter?.type]} alt="vault-icon" />
        </div>
        <div className="flex-1 flex flex-col gap-[0.4rem] border-l p-6 py-4 text-left">
          <button
            title={ac.name || '-'}
            onClick={() => {
              navigate(`/budgets/${budgetId}/accounting-centers/${acType}/${ac._id}`, {
                state: { acCenter: ac }
              });
            }}
            className="font-bold leading-[110%] capitalize truncate max-w-[150px] mb-[0.1rem] cursor-pointer text-left"
          >
            {ac.name || '-'}
          </button>
          <span className="text-xs text-secondary leading-[100%]">
            Remaining ACMEV €{ac?.acmev?.toLocaleString() || '-'}
          </span>
          <span className="text-xs text-secondary leading-[100%]">
            Acheived ACRT €{ac?.acrt_achieved?.toLocaleString() || '-'}
          </span>
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className={`w-[26px] h-[26px] flex items-center justify-center border rounded-full bg-lightPurple-200 ml-2 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-label={open ? 'Collapse' : 'Expand'}
          aria-expanded={open}
        >
          <img src={chevronDownIcon} alt="toggle" />
        </button>
      </div>
      <AccordionContent className="border-t" style={{ display: open ? 'block' : 'none' }}>
        <div className="flex items-start justify-between gap-0 mt-0">
          <PieGraph
            title="Remaining ACMEV"
            titleValue={(() => {
              if (graphViewMode === 'percentage' && acmevChartData) {
                return acmevChartData.length > 0 ? `${acmevChartData[0].value}%` : '0%';
              } else {
                const acmevTotal = ac?.acmev || 0;
                const acmevUsed = ac?.acmev_used || 0;
                const acmevRemaining = Math.max(0, acmevTotal - acmevUsed);
                return `€${acmevRemaining.toLocaleString()}`;
              }
            })()}
            chartConfig={acmevChartConfig}
            chartData={acmevChartData || []}
            className="bg-white border-r p-[0.85rem] flex-1 md:py-6 md:flex md:items-center md:justify-center md:flex-col"
            outerRadius={64}
            minHeight={140}
          />

          <PieGraph
            title="Achieved ACRT"
            titleValue={(() => {
              if (graphViewMode === 'percentage') {
                const achievedItem = acrtChartData && acrtChartData.find((item) => item.key === 'achieved');
                return achievedItem ? `${achievedItem.value}%` : '0%';
              } else {
                const acrtAchieved = ac?.acrt_achieved || 0;
                return `€${acrtAchieved.toLocaleString()}`;
              }
            })()}
            chartConfig={acrtChartConfig}
            chartData={acrtChartData || []}
            variant="primary"
            className="bg-white p-[0.85rem] flex-1 md:py-6 md:flex md:items-center md:justify-center md:flex-col"
            outerRadius={64}
            minHeight={140}
          />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export default ACItemAccordion;
